"use client"

import type React from "react"

import { useRef, useEffect, useState } from "react"
import Image from "next/image"
import { usePipStore } from "@/lib/pip-store"
import { useSettingsStore } from "@/lib/settings-store"
import { Slider } from "@/components/ui/slider"
import {
  X,
  Minimize2,
  Maximize2,
  Volume2,
  VolumeX,
  Play,
  Pause,
  Move,
  ChevronUp,
  ChevronDown,
  Gauge,
  Maximize,
} from "@/lib/icons"
import { cn } from "@/lib/utils"
import { getImageUrl } from "@/lib/image-utils"
import { useTranslations } from "next-intl"

export function PipPlayer() {
  const t = useTranslations("pipPlayer")
  const {
    pipPost,
    isPipActive,
    isPipMinimized,
    pipPosition,
    pipSize,
    pipMuted,
    pipVolume,
    pipPlaybackSpeed,
    closePip,
    togglePipMinimized,
    setPipPosition,
    setPipSize,
    setPipMuted,
    setPipVolume,
    setPipPlaybackSpeed,
  } = usePipStore()

  const { videoPitchCorrection, videoAutoMute } = useSettingsStore()

  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(true)
  const [showControls, setShowControls] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const [customPosition, setCustomPosition] = useState<{ x: number; y: number } | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const isVideo = pipPost?.fileUrl?.match(/\.(webm|mp4|mov|avi|mkv|flv|wmv|m4v|3gp|ogv)$/i)
  const isAnimated = pipPost?.fileUrl?.match(/\.(gif|apng|webp)$/i)

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = pipVolume
      videoRef.current.muted = pipMuted
      videoRef.current.playbackRate = pipPlaybackSpeed
      videoRef.current.preservesPitch = videoPitchCorrection
    }
  }, [pipVolume, pipMuted, pipPlaybackSpeed, videoPitchCorrection])

  useEffect(() => {
    if (videoRef.current && isPipActive && videoAutoMute) {
      setPipMuted(true)
    }
  }, [isPipActive, pipPost, videoAutoMute, setPipMuted])

  useEffect(() => {
    if (videoRef.current && isPipActive && !isPipMinimized) {
      videoRef.current.play().catch(() => {})
      setIsPlaying(true)
    }
  }, [isPipActive, isPipMinimized, pipPost])

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause()
      } else {
        const playPromise = videoRef.current.play()
        if (playPromise !== undefined) {
          playPromise.catch((error) => {
            if (error instanceof DOMException && error.name === "AbortError") return
            console.error("[v0] PiP video play failed:", error)
          })
        }
      }
      setIsPlaying(!isPlaying)
    }
  }

  const handleDragStart = (e: React.MouseEvent) => {
    if (!containerRef.current) return
    e.preventDefault()
    setIsDragging(true)
    const rect = containerRef.current.getBoundingClientRect()
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    })
  }

  const handleDragMove = (e: MouseEvent) => {
    if (!isDragging || !containerRef.current) return
    requestAnimationFrame(() => {
      const rect = containerRef.current!.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      let x = e.clientX - dragOffset.x
      let y = e.clientY - dragOffset.y

      const minX = 0
      const maxX = viewportWidth - rect.width
      const minY = 80 // account for header
      const maxY = viewportHeight - rect.height

      // Clamp to viewport bounds
      x = Math.max(minX, Math.min(maxX, x))
      y = Math.max(minY, Math.min(maxY, y))

      setCustomPosition({ x, y })
    })
  }

  const handleDragEnd = () => {
    setIsDragging(false)
  }

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleDragMove)
      window.addEventListener("mouseup", handleDragEnd)
      return () => {
        window.removeEventListener("mousemove", handleDragMove)
        window.removeEventListener("mouseup", handleDragEnd)
      }
    }
  }, [isDragging, dragOffset])

  const handlePositionChange = (pos: "bottom-right" | "bottom-left" | "top-right" | "top-left") => {
    setCustomPosition(null)
    setPipPosition(pos)
  }

  const cyclePlaybackSpeed = () => {
    const speeds = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 2.25, 2.5, 2.75, 3]
    const currentIndex = speeds.findIndex((s) => Math.abs(s - pipPlaybackSpeed) < 0.01)
    const nextIndex = (currentIndex + 1) % speeds.length
    setPipPlaybackSpeed(speeds[nextIndex])
  }

  const handleFullscreen = () => {
    closePip()
    // The modal should re-open automatically when PIP closes with the same post
    // This requires the parent component to track the last viewed post
  }

  if (!isPipActive || !pipPost) return null

  const sizeClasses = {
    small: "w-48 h-32",
    medium: "w-64 h-44",
    large: "w-80 h-56",
    xlarge: "w-[32rem] h-[22rem]",
  }

  const positionClasses = customPosition
    ? ""
    : {
        "bottom-right": "bottom-4 right-4",
        "bottom-left": "bottom-4 left-4",
        "top-right": "top-20 right-4",
        "top-left": "top-20 left-4",
      }[pipPosition]

  const customStyle = customPosition
    ? {
        left: `${customPosition.x}px`,
        top: `${customPosition.y}px`,
      }
    : {}

  return (
    <div
      ref={containerRef}
      className={cn(
        "fixed z-50 rounded-lg overflow-hidden shadow-2xl border border-border/50 bg-background",
        positionClasses,
        isPipMinimized ? "w-12 h-12" : sizeClasses[pipSize],
        isDragging && "cursor-grabbing",
      )}
      style={customStyle}
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => !isDragging && setShowControls(false)}
    >
      {isPipMinimized ? (
        <button
          onClick={togglePipMinimized}
          className="w-full h-full flex items-center justify-center bg-primary/20 hover:bg-primary/30 transition-colors"
          aria-label={t("restorePictureInPicture")}
          title={t("restorePictureInPicture")}
        >
          <Maximize2 className="h-5 w-5" />
        </button>
      ) : (
        <>
          <div className="relative w-full h-full">
            {isVideo ? (
              <video
                ref={videoRef}
                src={getImageUrl(pipPost.fileUrl)}
                className="w-full h-full object-cover"
                loop
                playsInline
                preload="metadata"
                autoPlay
                muted={pipMuted}
                onError={(e) => {
                  console.warn("[PipPlayer] Video load error:", e.currentTarget.error?.message)
                }}
              />
            ) : (
              <Image
                src={getImageUrl(pipPost.previewUrl || pipPost.fileUrl) || "/placeholder.svg"}
                alt={pipPost.tags?.slice(0, 5).join(", ") || "PIP image"}
                fill
                className="object-cover"
                unoptimized
              />
            )}

            <div
              className={cn(
                "absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/40",
                "transition-opacity duration-200",
                showControls ? "opacity-100" : "opacity-0",
              )}
            >
              <div className="absolute top-0 left-0 right-0 p-1.5 flex items-center justify-between">
                <button
                  onMouseDown={handleDragStart}
                  className="p-1 rounded hover:bg-white/20 cursor-grab active:cursor-grabbing"
                  title={t("dragToMove")}
                  aria-label={t("dragToMove")}
                >
                  <Move className="h-3.5 w-3.5 text-white" />
                </button>

                <div className="flex items-center gap-1">
                  <button
                    onClick={handleFullscreen}
                    className="p-1 rounded hover:bg-white/20"
                    title={t("fullscreen")}
                    aria-label={t("fullscreen")}
                  >
                    <Maximize className="h-3.5 w-3.5 text-white" />
                  </button>
                  <button
                    onClick={togglePipMinimized}
                    className="p-1 rounded hover:bg-white/20"
                    title={t("minimize")}
                    aria-label={t("minimizePictureInPicture")}
                  >
                    <Minimize2 className="h-3.5 w-3.5 text-white" />
                  </button>
                  <button
                    onClick={closePip}
                    className="p-1 rounded hover:bg-white/20"
                    title={t("closePictureInPicture")}
                    aria-label={t("closePictureInPicture")}
                  >
                    <X className="h-3.5 w-3.5 text-white" />
                  </button>
                </div>
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-1.5 flex items-center justify-between gap-2">
                {isVideo && (
                  <>
                    <button
                      onClick={handlePlayPause}
                      className="p-1 rounded hover:bg-white/20"
                      aria-label={isPlaying ? t("pauseVideo") : t("playVideo")}
                      title={isPlaying ? t("pause") : t("play")}
                    >
                      {isPlaying ? (
                        <Pause className="h-3.5 w-3.5 text-white" />
                      ) : (
                        <Play className="h-3.5 w-3.5 text-white" />
                      )}
                    </button>

                    <div className="flex items-center gap-1 flex-1">
                      <button
                        onClick={() => setPipMuted(!pipMuted)}
                        className="p-1 rounded hover:bg-white/20"
                        aria-label={pipMuted ? t("unmute") : t("mute")}
                        title={pipMuted ? t("unmute") : t("mute")}
                      >
                        {pipMuted ? (
                          <VolumeX className="h-3.5 w-3.5 text-white" />
                        ) : (
                          <Volume2 className="h-3.5 w-3.5 text-white" />
                        )}
                      </button>
                      <Slider
                        value={[pipMuted ? 0 : pipVolume * 100]}
                        onValueChange={([v]) => {
                          setPipVolume(v / 100)
                          if (v > 0) setPipMuted(false)
                        }}
                        max={100}
                        step={1}
                        className="w-16"
                      />
                    </div>

                    <div className="relative">
                      <button
                        onClick={cyclePlaybackSpeed}
                        className="p-1 rounded hover:bg-white/20"
                        title={`Speed: ${pipPlaybackSpeed.toFixed(2)}x`}
                        aria-label={t("changePlaybackSpeed")}
                      >
                        <Gauge className="h-3.5 w-3.5 text-white" />
                        <span className="text-[9px] text-white font-mono absolute -bottom-1 -right-1 bg-black/60 px-0.5 rounded">
                          {pipPlaybackSpeed.toFixed(2)}x
                        </span>
                      </button>
                    </div>
                  </>
                )}

                <div className="flex items-center gap-0.5">
                  <button
                    onClick={() => setPipSize("small")}
                    className={cn(
                      "px-1 py-0.5 text-[10px] rounded",
                      pipSize === "small" ? "bg-white/30 text-white" : "text-white/60 hover:text-white",
                    )}
                  >
                    S
                  </button>
                  <button
                    onClick={() => setPipSize("medium")}
                    className={cn(
                      "px-1 py-0.5 text-[10px] rounded",
                      pipSize === "medium" ? "bg-white/30 text-white" : "text-white/60 hover:text-white",
                    )}
                  >
                    M
                  </button>
                  <button
                    onClick={() => setPipSize("large")}
                    className={cn(
                      "px-1 py-0.5 text-[10px] rounded",
                      pipSize === "large" ? "bg-white/30 text-white" : "text-white/60 hover:text-white",
                    )}
                  >
                    L
                  </button>
                  <button
                    onClick={() => setPipSize("xlarge")}
                    className={cn(
                      "px-1 py-0.5 text-[10px] rounded",
                      pipSize === "xlarge" ? "bg-white/30 text-white" : "text-white/60 hover:text-white",
                    )}
                  >
                    XL
                  </button>
                </div>
              </div>

              <button
                onClick={() => handlePositionChange("top-left")}
                className={cn(
                  "absolute top-7 left-1 p-0.5 rounded hover:bg-white/20 my-1 ml-0.5",
                  pipPosition === "top-left" && !customPosition && "bg-white/30",
                )}
                aria-label={t("moveToTopLeft")}
                title={t("moveToTopLeft")}
              >
                <ChevronUp className="h-3 w-3 text-white -rotate-45" />
              </button>
              <button
                onClick={() => handlePositionChange("top-right")}
                className={cn(
                  "absolute top-7 right-1 p-0.5 rounded hover:bg-white/20 my-1 mr-0.5",
                  pipPosition === "top-right" && !customPosition && "bg-white/30",
                )}
                aria-label={t("moveToTopRight")}
                title={t("moveToTopRight")}
              >
                <ChevronUp className="h-3 w-3 text-white rotate-45" />
              </button>
              <button
                onClick={() => handlePositionChange("bottom-left")}
                className={cn(
                  "absolute bottom-7 left-1 p-0.5 rounded hover:bg-white/20 my-1 ml-0.5",
                  pipPosition === "bottom-left" && !customPosition && "bg-white/30",
                )}
                aria-label={t("moveToBottomLeft")}
                title={t("moveToBottomLeft")}
              >
                <ChevronDown className="h-3 w-3 text-white rotate-45" />
              </button>
              <button
                onClick={() => handlePositionChange("bottom-right")}
                className={cn(
                  "absolute bottom-7 right-1 p-0.5 rounded hover:bg-white/20 my-1 mr-0.5",
                  pipPosition === "bottom-right" && !customPosition && "bg-white/30",
                )}
                aria-label={t("moveToBottomRight")}
                title={t("moveToBottomRight")}
              >
                <ChevronDown className="h-3 w-3 text-white -rotate-45" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
